import type { ParticipantEntry } from '../api/participantEntries';

export type EntriesPerPeriodPoint = {
  label: string; // e.g. '2026-01'
  count: number;
};

export type Trend30d = {
  last30: number;
  prev30: number;
  delta: number;
  deltaPct: number | null;
};

export type ContestBreakdownRow = {
  contest_id: number;
  contest_name: string;
  entries: number;
  bestRanking: number | null;
  lastDate: string | null; // ISO
};

export type ParticipantAnalytics = {
  totalEntries: number;
  distinctContests: number;
  firstEntryAt: string | null;
  lastEntryAt: string | null;
  entriesPerMonth: EntriesPerPeriodPoint[];
  contestBreakdown: ContestBreakdownRow[];
  bestPodiums: number; // how many entries with ranking 1..3
  trend30d: Trend30d;
  topContest: ContestBreakdownRow | null;
};

const isoMonth = (d: Date) => {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

const toDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

export const computeParticipantAnalytics = (entries: ParticipantEntry[]): ParticipantAnalytics => {
  const safe = (entries ?? []).filter(Boolean);

  const dates = safe
    .map((e) => (e.created_at ? new Date(e.created_at) : null))
    .filter((d): d is Date => !!d && !Number.isNaN(d.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  const firstEntryAt = dates.length ? dates[0].toISOString() : null;
  const lastEntryAt = dates.length ? dates[dates.length - 1].toISOString() : null;

  const monthMap = new Map<string, number>();
  for (const e of safe) {
    if (!e.created_at) continue;
    const d = new Date(e.created_at);
    if (Number.isNaN(d.getTime())) continue;
    const key = isoMonth(d);
    monthMap.set(key, (monthMap.get(key) ?? 0) + 1);
  }

  const entriesPerMonth = Array.from(monthMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([label, count]) => ({ label, count }));

  const contestMap = new Map<number, ContestBreakdownRow>();
  for (const e of safe) {
    const id = e.contest_id;
    const name = e.contest_name ?? `#${id}`;

    const row = contestMap.get(id) ?? {
      contest_id: id,
      contest_name: name,
      entries: 0,
      bestRanking: null,
      lastDate: null,
    };

    row.entries += 1;

    const r = typeof e.ranking === 'number' ? e.ranking : null;
    if (r !== null && (row.bestRanking === null || r < row.bestRanking)) {
      row.bestRanking = r;
    }

    if (e.created_at) {
      const t = new Date(e.created_at);
      if (!Number.isNaN(t.getTime())) {
        if (!row.lastDate || new Date(row.lastDate).getTime() < t.getTime()) {
          row.lastDate = t.toISOString();
        }
      }
    }

    contestMap.set(id, row);
  }

  const contestBreakdown = Array.from(contestMap.values()).sort((a, b) => {
    if (a.entries !== b.entries) return b.entries - a.entries;
    if ((a.bestRanking ?? 9999) !== (b.bestRanking ?? 9999)) return (a.bestRanking ?? 9999) - (b.bestRanking ?? 9999);
    return a.contest_name.localeCompare(b.contest_name);
  });

  const bestPodiums = safe.reduce((acc, e) => {
    const r = typeof e.ranking === 'number' ? e.ranking : null;
    if (r !== null && r >= 1 && r <= 3) return acc + 1;
    return acc;
  }, 0);

  // Trend: last 30 full days vs previous 30 full days (based on entry timestamps)
  const now = new Date();
  const msDay = 24 * 60 * 60 * 1000;
  const startLast30 = new Date(now.getTime() - 30 * msDay);
  const startPrev30 = new Date(now.getTime() - 60 * msDay);

  let last30 = 0;
  let prev30 = 0;
  for (const e of safe) {
    const d = toDate(e.created_at);
    if (!d) continue;
    if (d >= startLast30) last30 += 1;
    else if (d >= startPrev30) prev30 += 1;
  }
  const delta = last30 - prev30;
  const deltaPct = prev30 > 0 ? (delta / prev30) * 100 : null;
  const trend30d: Trend30d = { last30, prev30, delta, deltaPct };

  const topContest = contestBreakdown.length ? contestBreakdown[0] : null;

  return {
    totalEntries: safe.length,
    distinctContests: contestMap.size,
    firstEntryAt,
    lastEntryAt,
    entriesPerMonth,
    contestBreakdown,
    bestPodiums,
    trend30d,
    topContest,
  };
};
